const SAMPLE_INTERVAL_MS = 80;
/** Levels are quantized so React only re-renders on a visible change. */
const LEVEL_STEP = 0.05;

/**
 * Samples the microphone loudness of `stream` as a 0..1 level and reports it
 * through `onLevel`. Returns a stop function that releases the analyser;
 * it never stops the stream itself. Platforms without Web Audio get a no-op.
 */
export function startAudioLevelMeter(
  stream: MediaStream,
  onLevel: (level: number) => void
): () => void {
  const AudioCtx =
    typeof window !== "undefined"
      ? window.AudioContext || (window as any).webkitAudioContext
      : undefined;
  if (!AudioCtx) return () => {};

  let ctx: AudioContext;
  let analyser: AnalyserNode;
  try {
    ctx = new AudioCtx();
    analyser = ctx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.5;
    ctx.createMediaStreamSource(stream).connect(analyser);
  } catch {
    return () => {};
  }

  const bins = new Uint8Array(analyser.frequencyBinCount);
  let lastReported = -1;

  const timer = setInterval(() => {
    analyser.getByteFrequencyData(bins);
    let sum = 0;
    for (let i = 0; i < bins.length; i += 1) sum += bins[i];
    const average = sum / bins.length;
    // Ambient noise floor subtraction and vocal level scaling
    const normalized = Math.min(1, Math.max(0, (average - 12) / 85));
    const level = Math.round(normalized / LEVEL_STEP) * LEVEL_STEP;
    onLevel(level);
    lastReported = level;
  }, SAMPLE_INTERVAL_MS);

  return () => {
    clearInterval(timer);
    try {
      void ctx.close();
    } catch {}
  };
}
